import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Record } from '../../types/records';
import { getRecords, deleteRecord } from '../../services/recordService';
import ContentCard from '../../components/admin/ContentCard';
import { PlusCircle, Search } from 'lucide-react';

const adminPath = process.env.REACT_APP_ADMIN_PATH || '/sh-yonetim-kapisi-x7';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const AllContentPage: React.FC = () => {
  const [records, setRecords] = useState<Record[]>(() => getRecords());
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleDelete = (id: string) => {
    try {
      deleteRecord(id);
      setRecords(getRecords()); // Re-fetch records after deletion
    } catch (error) {
      console.error("Failed to delete record:", error);
      // Optionally, show an error message to the user
      alert("Silme sırasında bir hata oluştu.");
    }
  };

  const filteredRecords = useMemo(() => {
    if (!searchTerm) {
      return records;
    }
    return records.filter(record =>
      record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.cityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [records, searchTerm]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">Tüm İçerikler</h1>
        <button
          onClick={() => navigate(`${adminPath}/add-content`)}
          className="flex items-center px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
        >
          <PlusCircle size={20} className="mr-2" />
          Yeni İçerik Ekle
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <input
          type="text"
          placeholder="Başlık, şehir veya kategoride ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 pl-10 rounded-lg bg-blue-900/80 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white"
        />
        <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300" />
      </div>

      {filteredRecords.length > 0 ? (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredRecords.map(record => (
            <ContentCard
              key={record.id}
              record={record}
              onDelete={handleDelete}
              variants={itemVariants}
            />
          ))}
        </motion.div>
      ) : (
        <div className="text-center py-20 bg-blue-800/50 rounded-lg">
          <h3 className="text-2xl font-bold text-white">Henüz İçerik Eklenmemiş</h3>
          <p className="text-blue-200 mt-2 mb-6">Başlamak için ilk içeriğinizi ekleyin.</p>
          <button
            onClick={() => navigate(`${adminPath}/add-content`)}
            className="flex items-center mx-auto px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
          >
            <PlusCircle size={20} className="mr-2" />
            İlk İçeriği Ekle
          </button>
        </div>
      )}
    </div>
  );
};

export default AllContentPage;

